import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GenericService } from '../../generic.service';
import { MachineTypes, machineModels } from '../../../assets/data/deviceData';
import { environment } from '../../../environments/environment';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LandingService } from '../../landing-page/service/landing.service';
import { defaultLanguage } from '../../shared/constants/language.constants';
import { CrowdinService } from '../../shared/service/crowdin.service';
import { Subscription } from 'rxjs';
@Component({
    selector: 'app-esr-support',
    imports: [CommonModule, FormsModule],
    templateUrl: './esr-support.component.html',
    styleUrl: './esr-support.component.scss'
})
export class EsrSupportComponent {
  request: string = 'close';
  deviceData: any = {};
  deviceData$: any = null;
  machineTypes: any = MachineTypes;
  machineModels: any = machineModels;
  machineModel: any = '';
  accountNumber: any = '';
  accountName: any = '';
  city: any = '';
  state: any = '';
  zip: any = '';
  isBusinessHours: boolean = false;
  isLoading: boolean = true;
  devices: any[] = [];
  selectedDevice: any = null;
  machineImageUrl: string = '';
  routeId: string = '';
  translations: { [key: string]: string } = {};
  currentLanguage: string = defaultLanguage;
  languageOptions: Array<{ value: string }> = [];
  isLoadingTranslations = true;
  languageIndex: any;
  subs = new Subscription();

  constructor(
    private router: Router,
    private genericService: GenericService,
    private route: ActivatedRoute,
    private landingService: LandingService,
    private crowdinService: CrowdinService,
  ) {
    this.deviceData = this.genericService.getLocalStorage();
    this.loadMachineImage();
    this.route.paramMap.subscribe((params) => {
      const machineTypeParam = params.get('machineType');
      const machineType: number | null = machineTypeParam
        ? parseInt(machineTypeParam, 10)
        : null;
      this.routeId = params.get('id') || '';
      this.accountNumber = params.get('accountNumber') || '';
    });
  }

  ngOnInit() {
    this.languageOptions = this.landingService.getLanguageOptions();

    const stored = localStorage.getItem('deviceData');
    const selectedName = stored
      ? JSON.parse(stored).languageSelected || defaultLanguage
      : defaultLanguage;
    this.currentLanguage = selectedName;

    this.subs.add(
      this.landingService.languageName$.subscribe((langName) => {
        this.currentLanguage = langName;
        this.translations = this.crowdinService.getAllTranslations();
      }),
    );

    this.landingService.loadTranslations(this.currentLanguage);
    this.isBusinessHours = this.isCurrentTimeWithinBusinessHours();
  }

  async languageChange(event: any) {
    const selectedLang = event.target.value;
    await this.landingService.loadTranslations(selectedLang);
  }

  loadMachineImage() {
    if (!this.deviceData.selectedMachineType) {
      this.machineImageUrl = '';
      return;
    }
    this.landingService
      .getDeviceImageByModelFromModelName(this.deviceData.selectedMachineType)
      .subscribe((imgUrl) => {
        this.machineImageUrl = imgUrl;
      });
  }

  ngAfterViewInit() {
    this.addCustomFeild();
  }

  navigate(pageNme: any) {
    this.deviceData['languageSelected'] = this.currentLanguage;
    this.genericService.setLocalStorage(this.deviceData);
    this.genericService.deviceData.next(this.deviceData);
    if (pageNme === 'edit') this.router.navigate(['/esrEdit']);
    else if (pageNme === 'contactUs') this.router.navigate(['/contactUs']);
    else this.router.navigate(['/esrSupportDetails']);
  }

  getModelName() {
    let productLine, model: any;
    this.machineTypes.forEach((element: any) => {
      if (element.setValue == this.deviceData?.productLine)
        productLine = element.value[this.languageIndex];
    });
    return productLine + ', ' + this.deviceData.model;
  }

  addCustomFeild() {
    // Prepare custom data for chat initialization
    const customData = {
      accountNumber: this.accountNumber,
      accountName: this.accountName,
      machineModel: this.machineModel,
      state: this.state,
      zip: this.zip,
    };

    // Get country code from route or stored data
    const countryCode =
      this.route.snapshot.queryParamMap.get('countryCode') ||
      this.deviceData?.countryCode;

    // Initialize chat with custom fields using the reusable service method
    this.landingService.initializeChatCustomFields(customData, countryCode);
  }

  niceChat() {
    const element = document.querySelector(
      '[data-selector=GUIDE_CHANNEL_BUTTON]',
    ) as HTMLElement;

    element.style.visibility = 'visible';
    if (element) {
      element.click();
    }

    setTimeout(() => {
      const minimizeButton: any = document.querySelector(
        "[data-cy='header-minimize-window']",
      );

      // Add an event listener for the click event
      minimizeButton.addEventListener('click', function () {
        // Your action for minimizing the window
        // You can put more logic here to minimize the window
      });
    }, 4000);
  }

  ngOnDestroy(): void {
    if (this.deviceData$) {
      this.deviceData$.unsubscribe();
    }
    this.deviceData['languageSelected'] = this.currentLanguage;
    this.genericService?.setLocalStorage(this.deviceData);
    this.genericService?.deviceData.next(this.deviceData);
  }

  goBack() {
    history.go(-1);
  }

  navigateHomePage() {
    this.landingService.navigateToHomePage();
  }

  isCurrentTimeWithinBusinessHours(): boolean {
    const currentTime = new Date();
    const currentDay = currentTime.getUTCDay(); // 0 (Sunday) to 6 (Saturday)
    const currentHour = currentTime.getUTCHours();
    const currentMinute = currentTime.getUTCMinutes();

    const isDST = (date: Date) => {
      const year = date.getUTCFullYear();
      const startDST = new Date(Date.UTC(year, 2, 8)); // 2nd Sunday of March
      const endDST = new Date(Date.UTC(year, 10, 1)); // November 1st

      // Move startDST to the second Sunday of March
      startDST.setUTCDate(8 + ((7 - startDST.getUTCDay()) % 7));

      return date >= startDST && date < endDST;
    };

    const isInDST = isDST(currentTime);
    // Central Time offset: -6 during DST, -5 during Standard Time
    const offset = isInDST ? -5 : -6;
    // Convert current time to Central Time (CT) using the offset
    let currentCTHour = currentHour + offset;
    // Adjust if hour is outside the 24-hour range
    if (currentCTHour < 0) currentCTHour += 24;
    if (currentCTHour >= 24) currentCTHour -= 24;

    // Check if the current day is Monday to Friday
    const isWeekday = currentDay >= 1 && currentDay <= 5;

    // Business hours:
    // - DST: 7 AM to 3:30 PM (CDT)
    // - Standard: 8 AM to 4:30 PM (CST)
    const isWithinBusinessHours = isInDST
      ? (currentCTHour > 8 || (currentCTHour === 8 && currentMinute >= 0)) &&
        (currentCTHour < 16 || (currentCTHour === 16 && currentMinute <= 30))
      : (currentCTHour > 7 || (currentCTHour === 7 && currentMinute >= 0)) &&
        (currentCTHour < 15 || (currentCTHour === 15 && currentMinute <= 30));

    return isWeekday && isWithinBusinessHours;
  }
}
